import React, { useState, useEffect } from 'react';
import { useSchema } from '../../../contexts/SchemaContext';
import { createIndicatorCondition, createCompositeCondition, createCandlestickCondition } from '../../../utils/strategyUtils';
import IndicatorConditionBuilder from './IndicatorConditionBuilder';
import CompositeConditionBuilder from './CompositeConditionBuilder';
import CandlestickConditionBuilder from './CandlestickConditionBuilder';

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

const ChevronUpIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);

/**
 * VoteConditionBuilder - Builder for N-of-M confluence conditions
 *
 * @param {Object} props
 * @param {Object} props.condition - Current vote condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {boolean} props.disabled - Whether form is disabled
 * @param {Object} props.rulesSchema - Rules schema from API
 * @param {Object} props.indicatorSchemas - Indicator schemas from API
 */
const VoteConditionBuilder = ({
    condition,
    onChange,
    disabled = false,
    rulesSchema = null,
    indicatorSchemas = null,
}) => {
    const { advancedRulesInfo } = useSchema();
    const [expandedIndex, setExpandedIndex] = useState(null);

    const subConditions = condition?.conditions || [];
    const requiredVotes = condition?.requiredVotes || 2;

    const handleRequiredVotesChange = (value) => {
        const votes = Math.max(1, Math.min(parseInt(value, 10) || 1, subConditions.length || 1));
        onChange({ ...condition, requiredVotes: votes });
    };

    const handleAddSubCondition = (type) => {
        let newCond;
        if (type === 'INDICATOR') newCond = createIndicatorCondition();
        else if (type === 'COMPOSITE') newCond = createCompositeCondition();
        else if (type === 'CANDLESTICK') newCond = createCandlestickCondition();
        else return;

        const updated = {
            ...condition,
            conditions: [...subConditions, newCond],
        };
        // Auto-adjust requiredVotes if needed
        if (updated.requiredVotes > updated.conditions.length) {
            updated.requiredVotes = updated.conditions.length;
        }
        onChange(updated);
        setExpandedIndex(subConditions.length); // expand newly added
    };

    const handleSubConditionChange = (idx, updatedSub) => {
        const newConditions = [...subConditions];
        newConditions[idx] = updatedSub;
        onChange({ ...condition, conditions: newConditions });
    };

    const handleDeleteSubCondition = (idx) => {
        const newConditions = subConditions.filter((_, i) => i !== idx);
        const newVotes = Math.min(requiredVotes, newConditions.length || 1);
        onChange({ ...condition, conditions: newConditions, requiredVotes: newVotes });
        if (expandedIndex === idx) setExpandedIndex(null);
        else if (expandedIndex > idx) setExpandedIndex(expandedIndex - 1);
    };

    const getConditionTypeLabel = (type) => {
        switch (type) {
            case 'INDICATOR': return 'Indicator';
            case 'COMPOSITE': return 'Composite';
            case 'CANDLESTICK': return 'Candlestick';
            default: return type;
        }
    };

    const getConditionSummary = (cond) => {
        if (cond.conditionType === 'INDICATOR') {
            return `${cond.indicatorType || '?'} ${cond.operator || ''}`;
        }
        if (cond.conditionType === 'COMPOSITE') {
            const left = cond.leftIndicator?.type || cond.leftIndicator?.indicatorType || '?';
            const right = cond.rightIndicator?.type || cond.rightIndicator?.indicatorType || '?';
            return `${left} vs ${right}`;
        }
        if (cond.conditionType === 'CANDLESTICK') {
            return cond.patternType || 'Pattern';
        }
        return cond.conditionType;
    };

    return (
        <div className="space-y-4">
            <h4 className="text-sm font-medium text-base-content/70">
                Vote Condition (N-of-M)
            </h4>
            <div className="divider my-1"></div>

            {/* Info alert */}
            <div className="alert alert-info text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>At least <strong>{requiredVotes}</strong> of <strong>{subConditions.length}</strong> conditions must be true for this vote rule to trigger.</span>
            </div>

            {/* Required Votes */}
            <div className="form-control w-full max-w-xs">
                <label className="label">
                    <span className="label-text font-medium">Required Votes</span>
                    <span className="label-text-alt text-base-content/50">
                        Min: 1, Max: {subConditions.length || 1}
                    </span>
                </label>
                <input
                    type="number"
                    className="input input-bordered input-sm w-32"
                    value={requiredVotes}
                    onChange={(e) => handleRequiredVotesChange(e.target.value)}
                    min={1}
                    max={subConditions.length || 1}
                    disabled={disabled}
                />
            </div>

            {/* Sub-conditions */}
            <div className="space-y-2">
                <label className="label">
                    <span className="label-text font-medium">Sub-Conditions ({subConditions.length})</span>
                </label>

                {subConditions.length === 0 && (
                    <div className="py-4 text-center bg-base-200 rounded-lg border border-dashed border-base-300">
                        <p className="text-base-content/60 text-sm">No sub-conditions yet</p>
                        <p className="text-xs text-base-content/40">Add at least 2 conditions for a vote rule</p>
                    </div>
                )}

                {subConditions.map((sub, idx) => (
                    <div key={idx} className="card card-border bg-base-100">
                        {/* Sub-condition header */}
                        <div
                            className="flex items-center gap-2 px-3 py-2 bg-base-200 cursor-pointer"
                            onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
                        >
                            <button className="btn btn-ghost btn-xs btn-circle">
                                {expandedIndex === idx ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </button>
                            <span className="badge badge-sm badge-outline">{idx + 1}</span>
                            <span className="badge badge-sm badge-primary badge-outline">
                                {getConditionTypeLabel(sub.conditionType)}
                            </span>
                            <span className="text-sm flex-1 truncate text-base-content/70">
                                {getConditionSummary(sub)}
                            </span>
                            <button
                                className="btn btn-ghost btn-xs btn-circle text-error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSubCondition(idx);
                                }}
                                disabled={disabled}
                            >
                                <DeleteIcon />
                            </button>
                        </div>

                        {/* Sub-condition body */}
                        {expandedIndex === idx && (
                            <div className="p-3 border-t border-base-200">
                                {sub.conditionType === 'INDICATOR' && (
                                    <IndicatorConditionBuilder
                                        condition={sub}
                                        onChange={(updated) => handleSubConditionChange(idx, updated)}
                                        errors={{}}
                                        disabled={disabled}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                )}
                                {sub.conditionType === 'COMPOSITE' && (
                                    <CompositeConditionBuilder
                                        condition={sub}
                                        onChange={(updated) => handleSubConditionChange(idx, updated)}
                                        errors={{}}
                                        disabled={disabled}
                                        rulesSchema={rulesSchema}
                                        indicatorSchemas={indicatorSchemas}
                                    />
                                )}
                                {sub.conditionType === 'CANDLESTICK' && (
                                    <CandlestickConditionBuilder
                                        condition={sub}
                                        onChange={(updated) => handleSubConditionChange(idx, updated)}
                                        disabled={disabled}
                                        rulesSchema={rulesSchema}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Add sub-condition dropdown */}
            <div className="dropdown dropdown-top">
                <div tabIndex={0} role="button" className="btn btn-sm btn-outline gap-1" disabled={disabled}>
                    <AddIcon /> Add Sub-Condition
                </div>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-48">
                    <li><a onClick={() => handleAddSubCondition('INDICATOR')}>Indicator</a></li>
                    {advancedRulesInfo?.compositeSupported && (
                        <li><a onClick={() => handleAddSubCondition('COMPOSITE')}>Composite</a></li>
                    )}
                    {advancedRulesInfo?.candlestickSupported && (
                        <li><a onClick={() => handleAddSubCondition('CANDLESTICK')}>Candlestick</a></li>
                    )}
                </ul>
            </div>
        </div>
    );
};

export default VoteConditionBuilder;
